import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Table } from '@carboledger/ui';
import { ROUTES } from '@/app/routes';
import { buildTableLabels } from '@/config/tableLabels';
import { PageHeader } from '@/components/layout';
import { useTableState, useTranslations, UnitTypeContext, useUnitTypeHeader } from '@/hooks';
import { EMPTY_LIST } from '@/lib/utils';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import { useGetInventoryQuery } from '../api/inventoryApi';
import { buildInventoryColumns } from '../constants/inventoryColumns';
import { buildUnitHeaderOptions } from '../constants/inventoryUnits';
import styles from './InventoryPage.module.scss';

const getInventoryRowId = (row) => row.id;

// Monitor ▸ Inventory — one row per material at a facility, per the top bar's selection.
export const InventoryPage = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { module } = useParams();

  const table = useTableState();
  const { data, isFetching } = useGetInventoryQuery(table.queryParams);
  const rows = data?.rows ?? EMPTY_LIST;

  const { unitHeaderSelect, contextValue } = useUnitTypeHeader(rows, buildUnitHeaderOptions, t);

  const handleOpenMaterial = useCallback(
    (row) => navigate(ROUTES.inventoryDetail(module, row.id)),
    [navigate, module],
  );

  const columns = useMemo(
    () => buildInventoryColumns(t, { unitHeaderSelect, onOpenMaterial: handleOpenMaterial }),
    [t, unitHeaderSelect, handleOpenMaterial],
  );

  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  const handleSearchChange = useCallback(
    (value) => {
      table.setSearch(value);
      table.setPage(1);
    },
    [table],
  );

  const emptyState = useMemo(
    () => ({
      image: <img src={emptyBoxIllustration} alt="" width={200} height={200} />,
      title: t('INVENTORY_EMPTY_TITLE'),
      description: t('INVENTORY_EMPTY_DESCRIPTION'),
    }),
    [t],
  );

  const noResults = useMemo(
    () => ({
      title: t('INVENTORY_NO_RESULTS_TITLE'),
      description: t('COMMON_NO_RESULTS_DESCRIPTION'),
    }),
    [t],
  );

  return (
    <div className={styles.page}>
      <PageHeader title={t('INVENTORY_TITLE')} />
      <UnitTypeContext.Provider value={contextValue}>
        <Table
          columns={columns}
          data={rows}
          total={data?.totalCount ?? 0}
          loading={isFetching}
          getRowId={getInventoryRowId}
          searchable
          onSearchChange={handleSearchChange}
          onPageChange={table.setPage}
          onPageSizeChange={table.setPageSize}
          emptyState={emptyState}
          noResults={noResults}
          labels={tableLabels}
        />
      </UnitTypeContext.Provider>
    </div>
  );
};
